import React, { useState, useEffect } from 'react';
import axiosClient, { UPLOADS_BASE_URL } from '../../axios_client';
import AdminMainLayout from './AdminMainLayout';
import AddAdminPackages from './AddAdminPackages';
import UpdateAdminPackages from './UpdateAdminPackages';

const AdminPackages = () => {
    const [packages, setPackages] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [error, setError] = useState('');
    const [expandedPackage, setExpandedPackage] = useState(null);

    // Define base URL for images (adjust for production)
    const BASE_URL = UPLOADS_BASE_URL; // Use https://sunflower-resort.com for production

    useEffect(() => {
        fetchPackages();
    }, []);

    const fetchPackages = async () => {
        try {
            const response = await axiosClient.get('/packages', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            setPackages(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch packages');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this package?')) {
            try {
                await axiosClient.delete(`/packages/${id}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                fetchPackages();
            } catch (err) {
                setError(err.response?.data?.error || 'Failed to delete package');
            }
        }
    };

    const openUpdateModal = (pkg) => {
        setSelectedPackage(pkg);
        setIsUpdateModalOpen(true);
    };

    const togglePackage = (id) => {
        setExpandedPackage(expandedPackage === id ? null : id);
    };

    // Normalize image URL to ensure it starts with a slash
    const getImageUrl = (img) => {
        if (!img) return '/fallback-image.jpg';
        const path = img.startsWith('/') ? img : `/${img}`;
        return `${BASE_URL}${path}`;
    };

    return (
        <AdminMainLayout>
            <div className="min-h-screen bg-[#F3ECDE] p-4 sm:p-6">
                <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-[#12392B]">
                    Manage Packages
                </h1>
                {error && (
                    <p className="text-red-500 text-sm sm:text-base mb-3 sm:mb-4">
                        {error}
                    </p>
                )}
                <button
                    onClick={() => setIsAddModalOpen(true)}
                    className="mb-4 bg-[#12392B] text-white p-2 sm:p-3 rounded hover:bg-[#E6A71A] hover:text-[#12392B] transition text-sm sm:text-base"
                >
                    Add New Package
                </button>

                {/* Small Screen: Collapsible Cards */}
                <div className="md:hidden">
                    {packages.map((pkg) => (
                        <div
                            key={pkg.id}
                            className="bg-white p-4 rounded-lg shadow-md mb-4"
                        >
                            <div
                                className="flex justify-between items-center cursor-pointer"
                                onClick={() => togglePackage(pkg.id)}
                            >
                                <h2 className="text-lg sm:text-xl font-semibold text-[#12392B]">
                                    {pkg.package_name}
                                </h2>
                                <svg
                                    className="w-5 h-5 text-[#12392B]"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d={
                                            expandedPackage === pkg.id
                                                ? 'M18 12H6'
                                                : 'M12 5v14m7-7H5'
                                        }
                                    />
                                </svg>
                            </div>
                            {expandedPackage === pkg.id && (
                                <div className="mt-3">
                                    <p className="text-gray-600 text-sm">{pkg.description}</p>
                                    {pkg.Image ? (
                                        <img
                                            src={getImageUrl(pkg.Image)}
                                            alt={pkg.package_name}
                                            className="w-auto h-24 object-cover rounded mt-2"
                                            onError={(e) => {
                                                console.error(`Failed to load image: ${getImageUrl(pkg.Image)}`);
                                                e.target.src = '/fallback-image.jpg';
                                            }}
                                        />
                                    ) : (
                                        <p className="text-gray-500 text-sm mt-2">No image available</p>
                                    )}
                                    <ul className="list-disc list-inside mt-2 text-gray-700 text-sm">
                                        {pkg.short_descriptions.map((desc, index) => (
                                            <li key={index}>{desc}</li>
                                        ))}
                                    </ul>
                                    <div className="mt-3 flex space-x-2">
                                        <button
                                            onClick={() => openUpdateModal(pkg)}
                                            className="bg-[#E6A71A] text-[#12392B] p-2 rounded hover:bg-[#12392B] hover:text-white transition text-sm"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(pkg.id)}
                                            className="bg-[#B91C1C] text-white p-2 rounded hover:bg-red-700 transition text-sm"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Medium and Larger Screens: Table */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full bg-white rounded-lg shadow-md">
                        <thead>
                            <tr className="bg-[#12392B] text-white">
                                <th className="p-3 text-left text-sm font-semibold">Package Name</th>
                                <th className="p-3 text-left text-sm font-semibold">Description</th>
                                <th className="p-3 text-left text-sm font-semibold">Short Descriptions</th>
                                <th className="p-3 text-left text-sm font-semibold">Image</th>
                                <th className="p-3 text-left text-sm font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {packages.map((pkg) => (
                                <tr key={pkg.id} className="border-b">
                                    <td className="p-3 text-[#12392B]">{pkg.package_name}</td>
                                    <td className="p-3 text-gray-600">{pkg.description}</td>
                                    <td className="p-3 text-gray-700">
                                        <ul className="list-disc list-inside">
                                            {pkg.short_descriptions.map((desc, index) => (
                                                <li key={index}>{desc}</li>
                                            ))}
                                        </ul>
                                    </td>
                                    <td className="p-3">
                                        {pkg.Image ? (
                                            <img
                                                src={getImageUrl(pkg.Image)}
                                                alt={pkg.package_name}
                                                className="w-auto h-16 object-cover rounded"
                                                onError={(e) => {
                                                    console.error(`Failed to load image: ${getImageUrl(pkg.Image)}`);
                                                    e.target.src = '/fallback-image.jpg';
                                                }}
                                            />
                                        ) : (
                                            <span className="text-gray-500">No image</span>
                                        )}
                                    </td>
                                    <td className="p-3 flex space-x-2">
                                        <button
                                            onClick={() => openUpdateModal(pkg)}
                                            className="bg-[#E6A71A] text-[#12392B] p-2 rounded hover:bg-[#12392B] hover:text-white transition text-sm"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(pkg.id)}
                                            className="bg-[#B91C1C] text-white p-2 rounded hover:bg-red-700 transition text-sm"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <AddAdminPackages
                    isOpen={isAddModalOpen}
                    onClose={() => setIsAddModalOpen(false)}
                    onPackageAdded={fetchPackages}
                />
                <UpdateAdminPackages
                    isOpen={isUpdateModalOpen}
                    onClose={() => setIsUpdateModalOpen(false)}
                    packageData={selectedPackage}
                    onPackageUpdated={fetchPackages}
                />
            </div>
        </AdminMainLayout>
    );
};

export default AdminPackages;